import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import {
  type AddressRequestDto,
  CustomerAddressesApi,
  type CustomerAddressesApiAddressesAddressIdPutRequest,
  CustomersApi,
} from "mws-finance-ts-sdk";
import { MeApi, type V2MeAccountPatchRequest } from "mws-ts-v2-sdk";

import { getErrorMessage } from "~/utils/errors";

let customersApi: CustomersApi;
const getCustomersApi = () => {
  // reduce memory overhead by only using one instance
  if (!customersApi) {
    customersApi = new CustomersApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      axios,
    );
  }

  return customersApi;
};

let customerAddressesApi: CustomerAddressesApi;
const getCustomerAddressesApi = () => {
  // reduce memory overhead by only using one instance
  if (!customerAddressesApi) {
    customerAddressesApi = new CustomerAddressesApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      axios,
    );
  }

  return customerAddressesApi;
};

let meApi: MeApi;
const getMeApi = () => {
  // reduce memory overhead by only using one instance
  if (!meApi) {
    meApi = new MeApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      axios,
    );
  }

  return meApi;
};

export const getCurrentCustomer = async () => {
  const response = await getCustomersApi().customersCurrentGet();

  return response.data;
};

export const customerQueryKeys = createQueryKeys("customers", {
  currentDetails: () => ({
    queryKey: ["current"],
    queryFn: getCurrentCustomer,
    contextQueries: {
      profileStats: (platformId: MaybeRef<string>) => ({
        queryKey: ["stats"],
        queryFn: async () => {
          const response = await getCustomersApi().customersCurrentStatsGet({
            xPlatformKey: unref(platformId),
          });

          return response.data;
        },
      }),
    },
  }),
});

/**
 * Wrapper for the "current customer" query to be able to conveniently scope to a certain user id for proper cache management and enable it only when the user is logged in
 */
export const useCurrentCustomerQuery = () => {
  const { isLoggedIn } = useAuth();

  return useQuery({
    ...customerQueryKeys.currentDetails(),
    enabled: isLoggedIn,
  });
};

export const useDeleteAddressMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const i18n = useI18n();

  const onSuccess = () => {
    toast.success(
      i18n.t("myAccount.accountSettings.address.addressCard.deleteSuccess"),
    );

    queryClient.invalidateQueries({
      queryKey: customerQueryKeys.currentDetails().queryKey,
    });
  };

  const onError = (error: unknown) => {
    toast.error(getErrorMessage(error)[0]);
  };

  return useMutation({
    mutationFn: async (addressId: string) => {
      const response = await getCustomerAddressesApi().addressesAddressIdDelete(
        {
          addressId,
        },
      );

      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useUpdateAddressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: CustomerAddressesApiAddressesAddressIdPutRequest,
    ) => {
      const response =
        await getCustomerAddressesApi().addressesAddressIdPut(request);

      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.currentDetails().queryKey,
      }),
  });
};

export const useUpdateAddressSettingsMutation = () => {
  const queryClient = useQueryClient();

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  return useMutation({
    mutationFn: async (request: Partial<V2MeAccountPatchRequest>) => {
      const {
        defaultBillingAddressId,
        defaultShippingAddressId,
        businessIdentifiers,
      } = request;

      return getMeApi().v2MeAccountPatch({
        xPlatformKey: unref(platformId),
        v2MeAccountPatchRequest: {
          businessIdentifiers: businessIdentifiers ?? null,
          defaultShippingAddressId: defaultShippingAddressId ?? null,
          defaultBillingAddressId: defaultBillingAddressId ?? null,
        },
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.currentDetails().queryKey,
      }),
  });
};

export const useAddAddressToCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      customerId: string;
      address: AddressRequestDto;
    }) => {
      const response =
        await getCustomerAddressesApi().customersCustomerIdAddressesPost({
          customerId: request.customerId,
          adminCustomersAddressesAddressIdPutRequest: request.address,
        });

      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: customerQueryKeys.currentDetails().queryKey,
      }),
  });
};
